import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Calendar, Phone, MapPin, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = useMemo(() => [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/gallery", label: "Gallery" },
    { path: "/events", label: "Events" },
    { path: "/book", label: "Book the Hall" },
    { path: "/contact", label: "Contact" },
    { path: "/admin", label: "Admin", isAdmin: true },
  ], []);

  const publicNavLinks = useMemo(() => navLinks.filter(link => !link.isAdmin), [navLinks]);

  const isActive = (path) => location.pathname === path || (path === "/admin" && location.pathname.startsWith("/admin"));

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#1C1917] text-white py-2 hidden md:block">
        <div className="container-custom flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <MapPin size={14} />
              Whaplode Drove, Spalding
            </span>
            <span className="flex items-center gap-2">
              <Phone size={14} />
              01406 709653
            </span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? "header-glass shadow-sm" : "bg-[#FAFAF9]"
        }`}
        data-testid="main-header"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-12 h-12 bg-[#14532D] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl font-serif">E</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-[#1C1917] font-serif tracking-tight">
                  The Elizabethan Centre
                </h1>
                <p className="text-xs text-[#78716C]">Whaplode Drove</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {publicNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? "active text-[#14532D]" : ""}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/admin"
                className={`nav-link flex items-center gap-1.5 ${isActive("/admin") ? "active text-[#14532D]" : "text-[#78716C]"}`}
                data-testid="nav-admin"
              >
                <Lock size={14} />
                Admin
              </Link>
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/book">
                <Button 
                  className="btn-primary flex items-center gap-2"
                  data-testid="book-now-btn"
                >
                  <Calendar size={18} />
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-[#1C1917]"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile menu */}
      <div 
        className={`mobile-menu lg:hidden ${isMobileMenuOpen ? "open" : ""}`}
        data-testid="mobile-menu"
      >
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-xl font-bold font-serif text-[#1C1917]">Menu</h2>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 text-[#1C1917]"
            aria-label="Close menu"
            data-testid="close-mobile-menu-btn"
          >
            <X size={24} />
          </button>
        </div>

        <nav className="flex flex-col gap-4">
          {publicNavLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-lg py-2 border-b border-[#E7E5E4] ${
                isActive(link.path) ? "text-[#14532D] font-semibold" : "text-[#1C1917]"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/admin"
            className={`text-lg py-2 border-b border-[#E7E5E4] flex items-center gap-2 ${
              isActive("/admin") ? "text-[#14532D] font-semibold" : "text-[#78716C]"
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="mobile-nav-admin"
          >
            <Lock size={18} />
            Admin Login
          </Link>
        </nav>

        <div className="mt-8">
          <Link to="/book" onClick={() => setIsMobileMenuOpen(false)}>
            <Button className="btn-primary w-full flex items-center justify-center gap-2">
              <Calendar size={18} />
              Book Now
            </Button>
          </Link>
        </div>

        <div className="mt-8 pt-8 border-t border-[#E7E5E4]">
          <p className="text-sm text-[#78716C] flex items-center gap-2 mb-2">
            <MapPin size={14} />
            Whaplode Drove, Spalding
          </p>
          <p className="text-sm text-[#78716C] flex items-center gap-2">
            <Phone size={14} />
            01406 709653
          </p>
        </div>
      </div>
    </>
  );
};

export default Header;
